import { useCallback, useEffect, useRef, type PropsWithChildren } from 'react';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import { useAuthStore } from '@/store/auth-store';

const options = {
  api_host: String(import.meta.env.VITE_PUBLIC_POSTHOG_HOST || ''),
} as const;

/**
 * Intervalo de verificação do session_id (em milissegundos).
 * Verifica a cada 30 segundos se o session_id do Datadog mudou.
 */
const SESSION_ID_CHECK_INTERVAL = 30_000;

/**
 * Identifica o usuário no PostHog e no Datadog quando o auth state muda.
 */
function PostHogIdentifier() {
  const posthog = usePostHog();
  const user = useAuthStore((s) => s.user);
  const lastUidRef = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) {
      return;
    }

    if (user?.uid && user.uid !== lastUidRef.current) {
      posthog.identify(user.uid, {
        email: user.email ?? undefined,
        name: user.displayName ?? undefined,
      });
      import('@/services/datadog')
        .then((m) =>
          m.setDatadogUser({
            id: user.uid,
            email: user.email ?? undefined,
            name: user.displayName ?? undefined,
          }),
        )
        .catch((e) => console.warn('[PostHog] Datadog import failed:', e));
      lastUidRef.current = user.uid;
    } else if (!user && lastUidRef.current) {
      posthog.reset();
      import('@/services/datadog')
        .then((m) => m.setDatadogUser({}))
        .catch((e) => console.warn('[PostHog] Datadog import failed:', e));
      lastUidRef.current = null;
    }
  }, [posthog, user]);

  return null;
}

/**
 * Registra o trace_id atual do Datadog como propriedade global no PostHog,
 * permitindo correlação de eventos entre as duas ferramentas.
 */
function TraceIdRegistrar() {
  const posthog = usePostHog();
  const lastSessionIdRef = useRef<string | undefined>(undefined);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const getSessionIdRef = useRef<typeof import('@/utils/logger').getCurrentSessionId | null>(null);

  const registerTraceId = useCallback(async () => {
    if (!posthog) {
      return;
    }

    if (!getSessionIdRef.current) {
      const { getCurrentSessionId } = await import('@/utils/logger');
      getSessionIdRef.current = getCurrentSessionId;
    }
    const sessionId = getSessionIdRef.current();

    if (sessionId && sessionId !== lastSessionIdRef.current) {
      posthog.register({ dd_session_id: sessionId });
      lastSessionIdRef.current = sessionId;
    }
  }, [posthog]);

  useEffect(() => {
    if (!posthog) {
      return;
    }

    registerTraceId();

    intervalRef.current = setInterval(() => {
      registerTraceId();
    }, SESSION_ID_CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [posthog, registerTraceId]);

  return null;
}

const POSTHOG_API_KEY = String(import.meta.env.VITE_PUBLIC_POSTHOG_MY_MENLO_KEY || '');
const POSTHOG_FORCE_ENABLE = import.meta.env.VITE_ENABLE_POSTHOG_MY_MENLO === 'true';
const APP_ENV = String(import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development');
const IS_LOCAL = APP_ENV === 'local' || import.meta.env.DEV;

export function PostHogProvider({ children }: PropsWithChildren) {
  if (!POSTHOG_API_KEY || (IS_LOCAL && !POSTHOG_FORCE_ENABLE)) {
    if (IS_LOCAL) {
      console.info('[PostHog] Skipping initialization in local environment');
    }
    return <>{children}</>;
  }

  return (
    <PHProvider apiKey={POSTHOG_API_KEY} options={options}>
      <PostHogIdentifier />
      <TraceIdRegistrar />
      {children}
    </PHProvider>
  );
}
